import { ForensicsSearchAlarms } from '@hakimo-ui/hakimo/types';
import { ArrowDownCircleIcon } from '@heroicons/react/24/outline';
import { ChevronDoubleDownIcon } from '@heroicons/react/24/solid';
import type { FC } from 'react';
import AlarmCard from './AlarmCard';

interface Props {
  results: ForensicsSearchAlarms[] | undefined;
  isLoading: boolean;
  onLoadMore: () => void;
}

export const ForensicsAlarmsSection: FC<Props> = ({
  results,
  isLoading,
  onLoadMore,
}) => {
  const hasResults = results && results.length > 0;

  return (
    <div className="space-y-8 pb-8">
      <div className="mt-4 grid grid-cols-1 gap-4 p-4 sm:gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:gap-8">
        {results?.map((result, index) => (
          <div
            key={result.alarm_id}
            className="animate-in fade-in slide-in-from-bottom-4"
            style={{
              animationDelay: `${(index % 10) * 50}ms`,
              animationDuration: '400ms',
              animationFillMode: 'backwards',
            }}
          >
            <AlarmCard alarm={result} />
          </div>
        ))}
      </div>

      {hasResults && (
        <div className="relative flex items-center justify-center px-4">
          <div className="absolute inset-x-0 flex items-center px-8">
            <div className="via-onlight-line-3 dark:via-ondark-line-2 h-px w-full bg-gradient-to-r from-transparent to-transparent" />
          </div>

          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className="border-onlight-line-3 hover:border-primary-600 hover:bg-onlight-bg-2 dark:border-ondark-line-2 dark:bg-ondark-bg-1/80 dark:hover:border-primary-600 dark:hover:bg-ondark-bg-2 group relative flex items-center gap-3 rounded-xl border bg-white/80 px-8 py-4 shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
          >
            <div className="relative">
              {isLoading ? (
                <div className="border-onlight-text-3 border-t-primary-600 dark:border-ondark-text-3 dark:border-t-primary-600 h-6 w-6 animate-spin rounded-full border-2" />
              ) : (
                <>
                  <ArrowDownCircleIcon className="text-onlight-text-2 group-hover:text-primary-600 dark:text-ondark-text-2 dark:group-hover:text-primary-600 h-6 w-6 transition-transform duration-200 group-hover:scale-110" />
                  <ChevronDoubleDownIcon className="text-primary-600 absolute inset-0 h-6 w-6 animate-bounce opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                </>
              )}
            </div>

            <div className="flex flex-col items-start">
              <span className="text-onlight-text-2 group-hover:text-primary-600 dark:text-ondark-text-1 dark:group-hover:text-primary-400 text-sm font-bold transition-colors">
                {isLoading ? 'Loading Results...' : 'Load More Results'}
              </span>
              {!isLoading && (
                <span className="text-onlight-text-3 dark:text-ondark-text-3 text-xs font-medium">
                  Showing {results.length} alarm
                  {results.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Shimmer Effect on Hover */}
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-gray-400/20 to-transparent dark:via-white/10" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
};
